import LegitCookies from "./cookies";

export default class CookieLegitNotice {
  #target = null;
  #notice = null;
  #settings = null;
  #consentedTo = [];
  firstConsent = false;

  screens = ["notice", "preferences", "toggle-preferences"];

  consentCookieName = "cl_consent";

  cookieNames = ["cl_consent", "cl_essential", "cl_tracking", "cl_marketing"];

  defaultSettings = {
    baseUrl: null,
    userOpt: false,
    consentMode: false,
    cookieDuration: 182,
  };

  static #events = {
    "cookie-legit-init": [],
    "notice-loaded": [],
    "consent-given": [],
    "consent-updated": [],

  };

  constructor(target, userSettings) {
    this.#settings = Object.assign({}, this.defaultSettings, userSettings);
    this.#target = this.selectTarget(target);
    this.checkConsentCookies(true);
    this.insertNotice();
    this.maybeInjectScripts();
    return this;
  }

  checkConsentCookies(fromLoad = false) {
    LegitCookies.list()
      .filter((cookie) => cookie.name.startsWith("cl_"))
      .forEach((cookie) => {
        let cookieIndex = this.#consentedTo.findIndex(c => c.name === cookie.name)
        if (cookieIndex >= 0) {
          this.#consentedTo[cookieIndex].value = cookie.value;
        } else {
          this.#consentedTo.push(cookie)
        }
      });

    if (this.#settings.consentMode && this.#consentedTo.length > 0) {
      this.updateGTMConsent();
    }

    if (this.#consentedTo.length > 0 && !fromLoad) {
      CookieLegitNotice.#trigger("consent-given", { consentedTo: this.#consentedTo, firstConsent: this.firstConsent });
    }
  }

  selectTarget(target) {
    return document.querySelector(target);
  }

  insertNotice() {
    this.#notice = document.getElementById("cookie-banner");
    let screen = this.#consentedTo.length === 0 ? "notice" : "toggle-preferences";
    this.firstConsent = screen === 'notice';
    this.changeScreen(screen);
    this.setupEvents();

    if (this.#settings.userOpt) {
      this.setPrefToggles();
    }
  }

  async fetchNotice() {
    const request = await fetch(
      `${this.#settings.baseUrl}?action=get_cookie_notice`
    );
    return await request.json();
  }

  setupEvents() {
    if (this.#settings.userOpt) {
      this.#notice
        .querySelector(".cookies-pref")
        .addEventListener("click", () => this.toggleUserPreferences());
      this.#notice
        .querySelector(".cookies-save-pref")
        .addEventListener("click", () => this.savePreferences());
      this.#notice
        .querySelector(".cookies-accept-pref")
        .addEventListener("click", () => this.acceptAllCookies());
    } else {
      this.#notice
        .querySelector(".cookie-legit-deny-btn")
        .addEventListener("click", () => this.denyAllCookies());
    }

    this.#notice
      .querySelector(".cookie-preferences-change")
      .addEventListener("click", () => this.updatePreferences());
    this.#notice
      .querySelector(".cookies-accept")
      .addEventListener("click", () => this.acceptAllCookies());
  }

  toggleUserPreferences() {
    this.changeScreen("preferences");
  }

  acceptAllCookies() {
    this.changeScreen("toggle-preferences");
    this.cookieNames.forEach((cookieName) => {
      LegitCookies.create(cookieName, true, this.#settings.cookieDuration);
    });
    this.checkConsentCookies();
  }

  savePreferences() {
    this.changeScreen("toggle-preferences");
    this.cookieNames.forEach((cookieName) => {
      let value =
        cookieName === this.consentCookieName
          ? true
          : this.#notice.querySelector(`input[name=${cookieName}]`).checked;
      LegitCookies.create(cookieName, value, this.#settings.cookieDuration);
    });
    this.checkConsentCookies();
  }

  updatePreferences() {
    let screen = this.#settings.userOpt ? "preferences" : "notice";
    this.changeScreen(screen);
    CookieLegitNotice.#trigger("consent-updated");
    // this.checkConsentCookies();
  }

  denyAllCookies() {
    this.changeScreen("toggle-preferences");
    this.cookieNames.forEach((cookieName) => {
      let value = cookieName === this.consentCookieName ? true : false;
      LegitCookies.create(cookieName, value, this.#settings.cookieDuration);
    });
    this.checkConsentCookies();
  }

  changeScreen(activeScreen) {
    let inactiveScreens = this.screens.filter(
      (screen) => screen !== activeScreen
    );

    this.#notice.classList.remove(...inactiveScreens);
    this.#notice.classList.add(activeScreen);
  }

  setPrefToggles() {
    this.#consentedTo.forEach((cookie) => {
      let toggle = this.#notice.querySelector(`input[name=${cookie.name}]`);
      if (toggle) {
        toggle.checked = cookie.value === "true";
      }
    });
  }

  async maybeInjectScripts() {
    if (
      !this.#settings.userOpt ||
      LegitCookies.find(this.consentCookieName) !== "true"
    )
      return;
    let scriptReq = await fetch(
      `${this.#settings.baseUrl}?action=get_tracking_scripts`
    );

    let scriptsWithPlacement = await scriptReq.json();
    for (const placement in scriptsWithPlacement) {
      if (Object.hasOwnProperty.call(scriptsWithPlacement, placement)) {
        const scripts = scriptsWithPlacement[placement];
        Object.values(scripts).forEach((script) => {
          if (!this.isJSON(script)) {
            let tag = this.buildScript(placement, script);
            document.querySelector(placement).append(tag);
          } else {
            script = JSON.parse(script);
            for (const stype in script) {
              if (Object.hasOwnProperty.call(script, stype)) {
                const contents = script[stype];
                contents.forEach((content) => {
                  let tag = this.buildScript(
                    placement,
                    content,
                    stype === "srcs"
                  );
                  document.querySelector(placement).append(tag);
                });
              }
            }
          }
        });
      }
    }
  }

  updateGTMConsent() {
    if (typeof gtag === "function") {
      return gtag("consent", "update", this.getGTMConsentObject());
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      0: "consent",
      1: "update",
      2: this.getGTMConsentObject(),
    });
  }

  getGTMConsentObject() {
    return {
      ad_storage:
        LegitCookies.find("cl_marketing")?.value === "true"
          ? "granted"
          : "denied",
      ad_user_data:
        LegitCookies.find("cl_marketing")?.value === "true"
          ? "granted"
          : "denied",
      ad_personalization:
        LegitCookies.find("cl_marketing")?.value === "true"
          ? "granted"
          : "denied",
      analytics_storage:
        LegitCookies.find("cl_tracking")?.value === "true"
          ? "granted"
          : "denied",
      security_storage: "granted",
    };
  }

  isJSON(objectString) {
    try {
      let json = JSON.parse(objectString);
    } catch (e) {
      return false;
    }

    return true;
  }

  buildScript(placement, scriptContent, isSrc = false) {
    let tagName = placement === "head" ? "script" : "noscript";
    let tag = document.createElement(tagName);

    if (isSrc) {
      tag.src = scriptContent;
      return tag;
    }

    tag.textContent = scriptContent;
    return tag;
  }

  static subscribe(event, callback) {
    if (!this.#events[event])
      return console.warn(
        `Cannot subscribe to ${event} because it does not exist!`
      );
    if (typeof callback !== "function")
      return console.warn(
        `Expected callback for ${event} got ${typeof callback}`
      );
    this.#events[event].push(callback);
  }

  static #trigger(event, data) {
    const callbacks = this.#events[event];

    if (!callbacks)
      return console.warn(`Cannot trigger ${event} because it does not exist!`);

    for (let callback of callbacks) {
      callback(data);
    }
  }
}
